import {
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Circle,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  StackDivider,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import AnalyzeService from '@/client/services/api/AnalyzeService';
import AvatarUploadForm from '@/client/components/shared/forms/AvatarUploadForm';
import { CameraAdd } from '@/client/components/icons/ContinuIcons';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import ReactAvatarEditor from 'react-avatar-editor';
import User from '@/client/services/api/User';
import type { User as UserType } from '@/client/types/User';
import { useAuthStore } from '@/client/services/state/authStore';
import { useLearnerProfileStore } from '../state/learnerProfileStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useUploadImage } from '@/client/services/hooks/shared/images/useUploadImage';

// TODO: Work with Jason to come up with a single avatar editor/modal that can be used everywhere.

interface LearnerProfileAvatarProps {
  userData: UserType;
}

export default function LearnerProfileAvatar({ userData }: LearnerProfileAvatarProps) {
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { isActiveUser, setAccountSettingsModalIsOpen } = useLearnerProfileStore();
  const avatarRef = useRef<HTMLInputElement>(null);
  const { uploadImageMutation } = useUploadImage();
  const { setToast } = useToastStore();
  const { status } = uploadImageMutation;

  const defaultAvatar: {
    editorModalOpen: boolean;
    img: File | string;
    scale: number;
    croppedImg: null | File;
  } = {
    editorModalOpen: false,
    img: '',
    scale: 1.5,
    croppedImg: null,
  };

  const [avatar, setAvatar] = useState(defaultAvatar);

  const size = useBreakpointValue({ base: 250, md: 300 });

  const {
    image: userAvatarImage,
    full_name: fullName,
    job_title: jobTitle,
    linked_departments: linkedDepartments,
  } = userData;

  const department = linkedDepartments[0]?.name;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let fileObject;

    if (event.target.files && event.target.files.length > 0) {
      fileObject = event.target.files?.[0];
    }

    if (!fileObject) {
      setToast({
        show: true,
        status: 'error',
        title: t('global.imageUploadError'),
      });
      return;
    }

    // eslint-disable-next-line no-param-reassign
    event.target.value = '';

    setAvatar({
      ...avatar,
      img: fileObject,
      editorModalOpen: true,
    });
  };

  const updateUserAvatar = useMutation({
    mutationFn: (url: string) => User.updateAvatar(user._id, url),
    onSuccess: (image) => {
      queryClient.invalidateQueries({ queryKey: ['learner-profile-user-data'] });

      AnalyzeService.recordAvatars(image);

      setAvatar(defaultAvatar);

      setToast({
        show: true,
        status: 'success',
        title: t('modules.notifications.profile_updated'),
      });
    },
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('modules.notifications.canNotUpdateProfile'),
      }),
  });

  let editor: any = useRef(null);

  const setEditorRef = (ed: any) => {
    editor = ed;
  };

  const handleSlider = (value: number) =>
    setAvatar({
      ...avatar,
      scale: value * 0.075,
    });

  const convertUrlToFile = async (url: string): Promise<File> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' });
    } catch (error) {
      console.error('Error converting URL to File:', error);
      throw new Error('Failed to load image');
    }
  };

  const handleExistingImageClick = async () => {
    if (!userAvatarImage) {
      avatarRef.current?.click();
      return;
    }

    try {
      const file = await convertUrlToFile(userAvatarImage);
      setAvatar({
        ...avatar,
        img: file,
        editorModalOpen: true,
      });
    } catch (error) {
      setToast({
        show: true,
        status: 'error',
        title: t('global.imageUploadError'),
      });
    }
  };

  const handleSave = () => {
    if (editor) {
      const canvasScaled = editor.getImageScaledToCanvas();

      canvasScaled.toBlob((blob: Blob | null) => {
        if (blob) {
          const file = new File([blob], 'uploaded-img.jpeg', {
            type: 'image/jpeg',
          });

          uploadImageMutation.mutate({
            file,
            errorMessage: t('learners.profile.avatar.uploadError'),
            successActions: (cloudfrontLink) => updateUserAvatar.mutateAsync(cloudfrontLink),
          });
        }
      });
    }
  };

  return (
    <Stack
      direction={{ base: 'column', md: 'row' }}
      gap={{ base: 0, md: 8 }}
      alignItems={{ base: 'center', md: 'start' }}
    >
      <AvatarUploadForm
        isLoading={status === 'loading'}
        handleUpload={(file) =>
          uploadImageMutation.mutate({
            file,
            errorMessage: t('global.avatarUploadError'),
            successActions: (cloudfrontLink) => updateUserAvatar.mutateAsync(cloudfrontLink),
          })
        }
        uploadElement={
          <Box
            border="6px solid"
            borderColor="white"
            borderRadius="50%"
            marginTop={-20}
            zIndex={1}
            position="relative"
          >
            <Avatar
              variant="neutral"
              src={userAvatarImage}
              name={fullName}
              height="172px"
              width="172px"
            />

            {isActiveUser && (
              <>
                <Input
                  type="file"
                  display="none"
                  ref={avatarRef}
                  accept="image/*"
                  onChange={(e) => handleFileChange(e)}
                />

                <Circle
                  aria-label="Change profile picture"
                  position="absolute"
                  top={0}
                  right={0}
                  size="40px"
                  borderRadius="full"
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="white"
                  border="2px solid"
                  borderColor="neutral.100"
                  paddingLeft={1}
                  paddingBottom={0.5}
                  transition="background-color 400ms"
                  _hover={{
                    cursor: 'pointer',
                    backgroundColor: 'neutral.0',
                  }}
                  onClick={handleExistingImageClick}
                >
                  <CameraAdd boxSize={6} color="neutral.900" />
                </Circle>
              </>
            )}
          </Box>
        }
      />

      <Stack spacing={4} alignItems={{ base: 'center', md: 'start' }}>
        <Stack spacing={0} alignItems={{ base: 'center', md: 'start' }}>
          <Text fontSize="28px" fontWeight={600} color="baseBlack">
            {fullName}
          </Text>

          <HStack
            fontSize="16px"
            fontWeight={400}
            color="baseBlack"
            divider={department && <StackDivider borderColor="neutral.500" />}
          >
            <Text>{jobTitle}</Text>

            <Text>{department}</Text>
          </HStack>
        </Stack>

        {isActiveUser && (
          <OutlineButton width="fit-content" onClick={() => setAccountSettingsModalIsOpen(true)}>
            {t('learners.profile.editSettings')}
          </OutlineButton>
        )}
      </Stack>

      <Modal isOpen={avatar.editorModalOpen} onClose={() => setAvatar(defaultAvatar)}>
        <ModalOverlay />

        <ModalContent padding={0} width="fit-content">
          <ModalBody padding={4}>
            <Stack spacing={4}>
              <ReactAvatarEditor
                ref={setEditorRef}
                image={avatar.img}
                border={50}
                height={size}
                width={size}
                color={[0, 0, 0, 0.6]} // RGBA
                scale={avatar.scale}
                rotate={0}
                borderRadius={1000}
              />

              <Slider
                aria-label="slider-ex-1"
                defaultValue={20}
                min={1}
                max={100}
                onChange={(value: number) => handleSlider(value)}
              >
                <SliderTrack>
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb />
              </Slider>
            </Stack>
          </ModalBody>

          <ModalFooter
            display="flex"
            justifyContent="flex-end"
            paddingRight={4}
            boxShadow="0 -4px 8px 0 rgba(0, 0, 0, 0.04)"
          >
            <ButtonGroup gap={2}>
              <Button
                variant="unstyled"
                color="brand.button"
                marginRight={2}
                onClick={() => updateUserAvatar.mutateAsync('')}
              >
                {t('learners.profile.avatar.remove')}
              </Button>

              <Button variant="cancel" onClick={() => setAvatar(defaultAvatar)}>
                {t('learners.profile.avatar.close')}
              </Button>

              <Button ref={editor} onClick={() => handleSave()}>
                {t('learners.profile.avatar.save')}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
}
